const API_BASE_URL: string = import.meta.env.VITE_API_URL || "";

function withBase(path: string): string {
  return `${API_BASE_URL.replace(/\/$/, "")}${path}`;
}

export interface CollegeTeam {
  team_id: string;
  college_name: string;
  team_name: string;
  gender: "male" | "female";
  division: string;
  state: string;
  national_rank: number | null;
  scoring_avg_adj: number | null;
  roster_size: number | null;
  head_coach: string | null;
}

export interface CollegeTeamsResponse {
  items: CollegeTeam[];
  total: number;
  limit: number;
  offset: number;
  season: string;
}

export type SortColumn =
  | "college_name"
  | "team_name"
  | "gender"
  | "division"
  | "state"
  | "national_rank"
  | "scoring_avg_adj"
  | "roster_size"
  | "head_coach";
export type SortDirection = "asc" | "desc";

export interface FetchCollegeTeamsOptions {
  query?: string;
  season?: string;
  gender?: "male" | "female" | "";
  sortBy?: SortColumn;
  sortDir?: SortDirection;
  limit?: number;
  offset?: number;
}

export interface TeamStudent {
  student_id: string;
  name: string;
  is_active: boolean;
  hometown: string | null;
  state: string | null;
  gender: "male" | "female" | null;
  class_year: string | null;
  graduation_year: number | null;
  current_status: string | null;
  jgs_rank: number | null;
  jgs_scoring_differential: number | null;
  college_scoring_avg: number | null;
  college_national_rank: number | null;
}

export interface TeamDetailResponse {
  team: {
    team_id: string;
    name: string;
    gender: "male" | "female";
    division: string | null;
    region: string | null;
    head_coach: string | null;
    head_coach_email: string | null;
    team_url: string | null;
  };
  college: {
    college_id: string;
    name: string;
    state: string | null;
    conference: string | null;
    association: string | null;
    aliases: string[];
  };
  ranking: {
    season: string;
    national_rank: number | null;
    scoring_avg_adj: number | null;
    roster_size: number | null;
    roster_scoring_range: {
      min: number | null;
      max: number | null;
      p25: number | null;
      p50: number | null;
      p75: number | null;
    } | null;
  } | null;
  students: TeamStudent[];
  student_count: number;
  active_count: number;
}

export async function fetchTeamDetail(
  teamId: string,
  season?: string
): Promise<TeamDetailResponse> {
  const params = new URLSearchParams();
  if (season) params.set("season", season);

  const headers: Record<string, string> = {};
  try {
    const token = sessionStorage.getItem("recruit_ai_token");
    if (token) headers["Authorization"] = `Bearer ${token}`;
  } catch {
    // Ignore
  }

  const resp = await fetch(
    withBase(`/admin/college-teams/${encodeURIComponent(teamId)}?${params.toString()}`),
    {
      method: "GET",
      headers: Object.keys(headers).length ? headers : undefined,
      credentials: "include",
    }
  );

  const text = await resp.text();
  let data: TeamDetailResponse & { error?: string; message?: string };
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(`Invalid response: ${text}`);
  }

  if (!resp.ok) {
    const msg = data?.error || data?.message || text || `Request failed (${resp.status})`;
    throw new Error(msg);
  }

  return data;
}

export async function fetchCollegeTeams(
  options: FetchCollegeTeamsOptions = {}
): Promise<CollegeTeamsResponse> {
  const { query, season, gender, sortBy, sortDir, limit, offset } = options;
  const params = new URLSearchParams();
  if (query) params.set("q", query);
  if (season) params.set("season", season);
  if (gender) params.set("gender", gender);
  if (sortBy) params.set("sortBy", sortBy);
  if (sortDir) params.set("sortDir", sortDir);
  if (limit !== undefined) params.set("limit", String(limit));
  if (offset !== undefined) params.set("offset", String(offset));

  const headers: Record<string, string> = {};

  // Attach admin token if present
  try {
    const token = sessionStorage.getItem("recruit_ai_token");
    if (token) headers["Authorization"] = `Bearer ${token}`;
  } catch {
    // Ignore token retrieval errors
  }

  const resp = await fetch(withBase(`/admin/college-teams?${params.toString()}`), {
    method: "GET",
    headers: Object.keys(headers).length ? headers : undefined,
    credentials: "include",
  });

  const text = await resp.text();
  let data: CollegeTeamsResponse & { error?: string; message?: string };
  try {
    data = text
      ? JSON.parse(text)
      : { items: [], total: 0, limit: 50, offset: 0, season: "2025_fall" };
  } catch {
    throw new Error(`Invalid response: ${text}`);
  }

  if (!resp.ok) {
    const msg = data?.error || data?.message || text || `Request failed (${resp.status})`;
    throw new Error(msg);
  }

  return data;
}
